import React, { useState, useCallback, useEffect } from 'react';
import { Plus, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Info, Sparkles, Package, FlaskConical, Tag, ReceiptText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import SearchableSelect from '@/components/hpp/SearchableSelect';
import RacikanRow from '@/components/hpp/RacikanRow';
import { useSession } from '@/lib/SessionContext';
import FinanceInvoiceCard from '@/components/hpp/FinanceInvoiceCard';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtQty = (n) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const getHargaBeli = (item) => item?.harga_rupiah || 0;
const getHargaJual = (item) => item?.harga_jual || 0;

const hitungJasa = (ml) => {
  if (!ml || ml === 0) return 0;
  if (ml <= 30) return 3000;
  if (ml <= 100) return 4000;
  if (ml <= 250) return 5000;
  return 6000;
};

const safeFloat = (val) => {
  if (val == null) return 0;
  if (typeof val === 'number') return val;
  const parsed = parseFloat(String(val).replace(/,/g, '.'));
  return isNaN(parsed) ? 0 : parsed;
};

export default function MarketingProductCard({ product, index, botolList, tutupList, sprayList, bahanList, itemTambahanList = [], fullsetList = [], varianDatabase = [], onChange, onRemove, canRemove }) {
  const { member } = useSession();
  const [expanded, setExpanded] = useState(product?.forceExpanded || !product?.nama_product);

  // Status selalu locked untuk form ini
  const botolDariClient = product.botol_dari_client === true;
  const tutupDariClient = product.tutup_dari_client === true;
  const sprayDariClient = product.spray_dari_client === true;

  const botol = botolDariClient ? null : botolList.find(b => b.id === product.botol_id);
  const tutup = tutupDariClient ? null : tutupList.find(t => t.id === product.tutup_id);
  const spray = sprayDariClient ? null : sprayList.find(s => s.id === product.spray_id);
  
  const normalizeCode = (str) => String(str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  const fullset = fullsetList.find(f => String(f.id) === String(product.fullset_id)) ||
    (product.fullset_nama ? fullsetList.find(f => 
      normalizeCode(f.nama) === normalizeCode(product.fullset_nama) ||
      `${normalizeCode(f.nama)}${normalizeCode(f.catatan)}` === normalizeCode(product.fullset_nama) ||
      normalizeCode(product.fullset_nama).includes(normalizeCode(f.nama)) ||
      normalizeCode(f.nama).includes(normalizeCode(product.fullset_nama))
    ) : null) ||
    (product.botol_nama ? fullsetList.find(f => 
      normalizeCode(f.nama) === normalizeCode(product.botol_nama) ||
      normalizeCode(product.botol_nama).includes(normalizeCode(f.nama)) ||
      normalizeCode(f.nama).includes(normalizeCode(product.botol_nama))
    ) : null);

  const isFullsetMissingInDB = product.is_fullset && !fullset && Boolean(product.fullset_nama) && product.fullset_nama !== 'Dari Client' && product.fullset_nama !== 'Tanpa Fullset / Dari Client';
  const isBotolMissingInDB = !botol && !product.botol_dari_client && Boolean(product.botol_nama) && product.botol_nama !== 'Dari Client' && product.botol_nama !== 'Botol Dari Client' && !product.is_fullset;
  const isTutupMissingInDB = !tutup && !product.tutup_dari_client && Boolean(product.tutup_nama) && product.tutup_nama !== 'Dari Client' && product.tutup_nama !== 'Tutup Dari Client' && !product.is_fullset;
  const isSprayMissingInDB = !spray && !product.spray_dari_client && Boolean(product.spray_nama) && product.spray_nama !== 'Dari Client' && product.spray_nama !== 'Spray Dari Client' && !product.is_fullset;

  const hasMissingBahan = (product.racikan || []).some(r => {
    if (r.dari_client) return false;
    const rName = (r.bahan_nama || r.nama_bahan || '').trim().toLowerCase();
    if (!rName) return false;
    const match = bahanList.find(b => b.id === r.bahan_id || (b.nama || '').trim().toLowerCase() === rName);
    return !match;
  });

  const hasMissingItemTambahan = (product.komponen_tambahan || []).some(k => {
    if (k.dari_client) return false;
    const kName = (k.nama || k.item_nama || '').trim().toLowerCase();
    if (!kName) return false;
    const match = itemTambahanList.find(i => i.id === k.inventory_item_id || (i.nama || '').trim().toLowerCase() === kName);
    return !match;
  });

  const missingTypes = [];
  if (isFullsetMissingInDB || isBotolMissingInDB || isTutupMissingInDB || isSprayMissingInDB) missingTypes.push('Kemasan');
  if (hasMissingBahan) missingTypes.push('Bahan');
  if (hasMissingItemTambahan) missingTypes.push('Aksesoris');

  const isAnyItemMissingInDB = missingTypes.length > 0;
  const missingBadgeLabel = `${missingTypes.join(' & ')} Belum Terhubung Inventory`;

  const baseUkuran = botolDariClient ? (product.botol_ukuran_client || 0) : (botol?.ukuran_label_ml || product.botol_ukuran_client || parseFloat(product.selected_size) || 0);
  const ukuranLabelMl = product.custom_ukuran_ml !== undefined ? parseFloat(product.custom_ukuran_ml) || 0 : baseUkuran;
  const ukuranAktualMl = product.custom_ukuran_aktual_ml !== undefined ? parseFloat(product.custom_ukuran_aktual_ml) : (botolDariClient ? 0 : (botol?.ukuran_aktual_ml ?? 0));
  const ukuranLabel = (ukuranAktualMl > 0) ? ukuranAktualMl : ukuranLabelMl;

  const totalPersen = (product.racikan || []).reduce((s, r) => s + safeFloat(r.persentase), 0);
  const isValid = Math.abs(totalPersen - 100) < 0.01;

  // HPP / Modal calculation
  const modalBotol = botolDariClient ? 0 : getHargaBeli(botol);
  const modalTutup = tutupDariClient ? 0 : getHargaBeli(tutup);
  const modalSpray = sprayDariClient ? 0 : getHargaBeli(spray);
  let modalPackaging = product.is_fullset && fullset ? fullset.harga_rupiah : (modalBotol + modalTutup + modalSpray);

  const calcModalRacikan = (ukuran) => {
    return (product.racikan || []).reduce((sum, r) => {
      if (!r.bahan_id || !r.persentase) return sum;
      const vol = (safeFloat(r.persentase) / 100) * ukuran;
      const isLiter = r.satuan === 'g'; 
      const hargaBeliSatuan = r.harga_per_satuan || 0;

      if (isLiter) {
        const hargaPerMl = hargaBeliSatuan / 1000;
        return sum + vol * hargaPerMl;
      }
      const satuan = r.satuan || 'kg';
      const perMl = (satuan === 'kg' || satuan === 'liter' || satuan === 'ml' || satuan === 'gram') ? hargaBeliSatuan / 1000 : hargaBeliSatuan;
      return sum + perMl * vol;
    }, 0);
  };

  let modalRacikan = calcModalRacikan(ukuranLabel);

  const calcJualRacikan = (ukuran) => {
    return (product.racikan || []).reduce((sum, r) => {
      const hSatuan = (r.satuan || 'kg').toLowerCase();
      const isLiter = hSatuan === 'g';
      const hBeliSat = r.harga_per_satuan || 0;
      const hJual = r.harga_jual_satuan !== undefined ? r.harga_jual_satuan : 0;
      const hJualKecil = r.custom_harga_per_unit || (hSatuan === 'gram' ? hJual : hJual / 1000);
      const vMl = r.volume_ml || ((safeFloat(r.persentase) / 100) * ukuran);
      return sum + (hJualKecil * vMl);
    }, 0);
  };
  
  let jualRacikan = calcJualRacikan(ukuranLabel);

  const getKomponenPrice = (k) => {
    if (k.dari_client) return { modal: 0, jual: 0 };
    const name = k.item_nama || k.nama_item || k.nama;
    const found = itemTambahanList.find(i => i.nama === name);
    return {
      modal: found ? (found.harga_rupiah || 0) : (k.harga || 0),
      jual: found ? (found.harga_jual || 0) : (k.harga_jual || 0)
    };
  };

  const modalKomponen = (product.komponen_tambahan || []).reduce((sum, k) => sum + (getKomponenPrice(k).modal * (k.qty || 1)), 0);
  const jualKomponen = (product.komponen_tambahan || []).reduce((sum, k) => sum + (getKomponenPrice(k).jual * (k.qty || 1)), 0);
  
  // Custom override dari DB
  const varianObj = varianDatabase.find(v => String(v.id).toLowerCase() === String(product.variant_id).toLowerCase());
  const displayVariantId = varianObj ? varianObj.id : product.variant_id;
  const currentSizeStr = String(product.selected_size || ukuranLabelMl || '');
  const matchedSizeObj = varianObj?.sizes?.find(s => 
    String(s.size) === currentSizeStr || 
    parseFloat(s.size) === parseFloat(currentSizeStr) || 
    parseFloat(s.size) === parseFloat(ukuranLabelMl)
  );
  const dbBahan = matchedSizeObj?.bahan;
  const customHargaJual = dbBahan?.custom_harga_jual;
  const customMargin = dbBahan?.custom_margin;
  const customJasa = dbBahan?.custom_jasa;

  const otomatisJasa = (customJasa !== undefined && customJasa !== null && customJasa !== '') ? Number(customJasa) : hitungJasa(ukuranLabelMl);
  const loadedJasa = (product.jasa_nominal !== undefined && product.jasa_nominal !== null && product.jasa_nominal !== 0 && product.jasa_nominal !== '') 
    ? product.jasa_nominal 
    : otomatisJasa;
  const jasaNominal = loadedJasa;

  let modalTotalPerPcs = 0;
  let calculatedJualTotal = 0;
  let pureSistemTotal = 0;
  let modalMurni = 0;
  let jualBotol = 0;
  let jualTutup = 0;
  let jualSpray = 0;
  let jualPackagingManual = 0;
  
  let aggregatedKemasan = [];
  let aggregatedCairan = [];

  if (product.is_discovery_set) {
    let subModalPack = 0;
    let subJualPack = 0;
    let subModalRacikan = 0;
    let subJualRacikan = 0;
    let subJasaTotal = 0;

    (product.items || []).forEach(it => {
      const iBotol = botolList.find(b => b.id === it.botol_id);
      const iTutup = tutupList.find(t => t.id === it.tutup_id);
      const iSpray = sprayList.find(s => s.id === it.spray_id);
      const iFullset = fullsetList.find(f => f.id === it.fullset_id);
      
      if (iBotol && !it.botol_dari_client) {
        subModalPack += getHargaBeli(iBotol);
        subJualPack += getHargaJual(iBotol);
      }
      
      const regexMatch = it.nama_variant?.match(/\((\d+)ml\)/);
      const fallbackMl = regexMatch ? parseFloat(regexMatch[1]) : (it.botol_ukuran_client || 0);
      const iUkuranLabel = iBotol?.ukuran_label_ml || fallbackMl;
      const iUkuranAktual = it.custom_ukuran_aktual_ml !== undefined ? it.custom_ukuran_aktual_ml : (iBotol?.ukuran_aktual_ml || fallbackMl);
      const iUkuranDipakai = it.custom_ukuran_aktual_ml !== undefined ? iUkuranAktual : iUkuranLabel;

      subModalRacikan += (it.racikan || []).reduce((sum, r) => {
        if (!r.bahan_id || !r.persentase) return sum;
        const vol = (safeFloat(r.persentase) / 100) * iUkuranDipakai;
        const hSatuan = (r.satuan || 'kg').toLowerCase();
        const hargaBeliSatuan = r.harga_per_satuan || 0;
        const perMl = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'ml' || hSatuan === 'gram' || hSatuan === 'g') ? hargaBeliSatuan / 1000 : hargaBeliSatuan;
        return sum + perMl * vol;
      }, 0);

      subJualRacikan += (it.racikan || []).reduce((sum, r) => {
        const hSatuan = (r.satuan || 'kg').toLowerCase();
        const isLiter = hSatuan === 'g';
        const hJualSat = r.harga_jual_satuan !== undefined ? r.harga_jual_satuan : 0;
        const hJualKecil = r.custom_harga_per_unit || (hSatuan === 'gram' ? hJualSat : hJualSat / 1000);
        const vMl = r.volume_ml || ((safeFloat(r.persentase) / 100) * iUkuranDipakai);
        return sum + (hJualKecil * vMl);
      }, 0);

      // Add Jasa for each internal item to match the Database Brand calculation
      subJasaTotal += hitungJasa(iUkuranDipakai);
      
      // Aggregate Kemasan
      if (it.is_fullset && iFullset) {
        const found = aggregatedKemasan.find(k => k.nama === iFullset.nama && k.isFullset);
        if (found) { found.qty += 1; found.modal += iFullset.harga_rupiah || 0; found.jual += getHargaJual(iFullset); }
        else aggregatedKemasan.push({ nama: iFullset.nama || 'Fullset', modal: iFullset.harga_rupiah || 0, jual: getHargaJual(iFullset), qty: 1, isFullset: true });
      } else {
        if (iBotol && !it.botol_dari_client) {
          const found = aggregatedKemasan.find(k => k.nama === iBotol.nama && !k.isFullset);
          if (found) { found.qty += 1; found.modal += getHargaBeli(iBotol); found.jual += getHargaJual(iBotol); }
          else aggregatedKemasan.push({ nama: iBotol.nama, modal: getHargaBeli(iBotol), jual: getHargaJual(iBotol), qty: 1, isFullset: false });
        }
        if (iTutup && !it.tutup_dari_client) {
          const found = aggregatedKemasan.find(k => k.nama === iTutup.nama && !k.isFullset);
          if (found) { found.qty += 1; found.modal += getHargaBeli(iTutup); found.jual += getHargaJual(iTutup); }
          else aggregatedKemasan.push({ nama: iTutup.nama, modal: getHargaBeli(iTutup), jual: getHargaJual(iTutup), qty: 1, isFullset: false });
        }
        if (iSpray && !it.spray_dari_client) {
          const found = aggregatedKemasan.find(k => k.nama === iSpray.nama && !k.isFullset);
          if (found) { found.qty += 1; found.modal += getHargaBeli(iSpray); found.jual += getHargaJual(iSpray); }
          else aggregatedKemasan.push({ nama: iSpray.nama, modal: getHargaBeli(iSpray), jual: getHargaJual(iSpray), qty: 1, isFullset: false });
        }
      }

      // Aggregate Cairan
      (it.racikan || []).forEach(r => {
        if (!r.bahan_id || !r.persentase) return;
        const volPerPcs = (safeFloat(r.persentase) / 100) * iUkuranDipakai;
        const hSatuan = (r.satuan || 'kg').toLowerCase();
        const hargaBeliSatuan = r.harga_per_satuan || 0;
        const perMl = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'ml' || hSatuan === 'gram' || hSatuan === 'g') ? hargaBeliSatuan / 1000 : hargaBeliSatuan;
        const cost = perMl * volPerPcs;

        const hJualSat = r.harga_jual_satuan !== undefined ? r.harga_jual_satuan : 0;
        const hJualPerMl = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'ml' || hSatuan === 'gram' || hSatuan === 'g') ? hJualSat / 1000 : hJualSat;
        const jualCost = r.custom_harga_per_unit ? (r.custom_harga_per_unit * volPerPcs) : (hJualPerMl * volPerPcs);
        
        const found = aggregatedCairan.find(c => c.bahan_id === r.bahan_id);
        if (found) {
           found.volPerPcs += volPerPcs;
           found.cost += cost;
           found.jualCost = (found.jualCost || 0) + jualCost;
        } else {
           aggregatedCairan.push({
             bahan_id: r.bahan_id,
             bahan_nama: r.bahan_nama,
             volPerPcs,
             satuan: r.satuan,
             kategori: r.kategori,
             cost,
             jualCost
           });
        }
      });
    });

    pureSistemTotal = Math.round(subJualPack + subJualRacikan + subJasaTotal + jasaNominal + jualKomponen);
    const totalSemuaInternal = Math.round(subModalPack + subModalRacikan + modalKomponen + jasaNominal);
    if (customHargaJual !== undefined && customHargaJual !== null && Number(customHargaJual) > 0) {
        calculatedJualTotal = Number(customHargaJual);
    } else if (customMargin !== undefined && customMargin !== null && customMargin !== '') {
        calculatedJualTotal = Math.round(pureSistemTotal + Number(customMargin));
    } else {
        calculatedJualTotal = pureSistemTotal;
    }
    
    modalTotalPerPcs = totalSemuaInternal;
    modalMurni = subModalPack + subModalRacikan + modalKomponen;
    
    // Override the rendered variables for Discovery Set so UI displays them correctly
    modalPackaging = subModalPack;
    jualPackagingManual = subJualPack + subJasaTotal;
    modalRacikan = subModalRacikan;
    jualRacikan = subJualRacikan;
  } else {
    modalMurni = modalPackaging + modalRacikan + modalKomponen;
    
    // Harga Jual Default (Inventori)
    const jualBotolDef = botolDariClient ? 0 : getHargaJual(botol);
    const jualTutupDef = tutupDariClient ? 0 : getHargaJual(tutup);
    const jualSprayDef = sprayDariClient ? 0 : getHargaJual(spray);
    
    jualBotol = product.botol_harga_jual !== undefined ? product.botol_harga_jual : jualBotolDef;
    jualTutup = product.tutup_harga_jual !== undefined ? product.tutup_harga_jual : jualTutupDef;
    jualSpray = product.spray_harga_jual !== undefined ? product.spray_harga_jual : jualSprayDef;
    
    jualPackagingManual = product.is_fullset && fullset
      ? (product.fullset_harga_jual !== undefined ? product.fullset_harga_jual : getHargaJual(fullset))
      : (jualBotol + jualTutup + jualSpray);
    
    pureSistemTotal = Math.round(jualPackagingManual + jualRacikan + jasaNominal + jualKomponen);
    modalTotalPerPcs = Math.round(modalMurni + jasaNominal);
    if (customHargaJual !== undefined && customHargaJual !== null && Number(customHargaJual) > 0) {
        calculatedJualTotal = Number(customHargaJual);
    } else if (customMargin !== undefined && customMargin !== null && customMargin !== '') {
        calculatedJualTotal = Math.round(pureSistemTotal + Number(customMargin));
    } else {
        calculatedJualTotal = pureSistemTotal;
    }
    
    // Populate for single product
    if (product.is_fullset) {
       if (fullset) aggregatedKemasan.push({ nama: fullset.nama || 'Fullset', modal: modalPackaging, jual: jualPackagingManual, qty: 1, isFullset: true });
    } else {
       if (botol && !botolDariClient) aggregatedKemasan.push({ nama: botol.nama || 'Botol', modal: modalBotol, jual: jualBotol, qty: 1, isFullset: false });
       if (tutup && !tutupDariClient) aggregatedKemasan.push({ nama: tutup.nama || 'Tutup', modal: modalTutup, jual: jualTutup, qty: 1, isFullset: false });
       if (spray && !sprayDariClient) aggregatedKemasan.push({ nama: spray.nama || 'Spray', modal: modalSpray, jual: jualSpray, qty: 1, isFullset: false });
    }

    (product.racikan || []).forEach(r => {
      if (!r.bahan_id || !r.persentase) return;
      const volPerPcs = (safeFloat(r.persentase) / 100) * ukuranLabel;
      const hSatuan = (r.satuan || 'kg').toLowerCase();
      const hargaBeliSatuan = r.harga_per_satuan || 0;
      const perMl = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'ml' || hSatuan === 'gram' || hSatuan === 'g') ? hargaBeliSatuan / 1000 : hargaBeliSatuan;
      const cost = perMl * volPerPcs;
      
      const hJualSat = r.harga_jual_satuan !== undefined ? r.harga_jual_satuan : 0;
      const hJualPerMl = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'ml' || hSatuan === 'gram' || hSatuan === 'g') ? hJualSat / 1000 : hJualSat;
      const jualCost = r.custom_harga_per_unit ? (r.custom_harga_per_unit * volPerPcs) : (hJualPerMl * volPerPcs);

      aggregatedCairan.push({
         bahan_id: r.bahan_id,
         bahan_nama: r.bahan_nama,
         volPerPcs,
         satuan: r.satuan,
         kategori: r.kategori,
         cost,
         jualCost
      });
    });
  }
  
  const inputHargaJual = product.harga_jual_per_pcs !== undefined ? product.harga_jual_per_pcs : '';
  const defaultHargaJualAkhir = (customHargaJual !== undefined && customHargaJual !== null) ? customHargaJual : calculatedJualTotal;
  const finalHargaJualPerPcs = inputHargaJual !== '' ? inputHargaJual : defaultHargaJualAkhir;

  // Margin
  const marginNominal = finalHargaJualPerPcs - modalMurni;
  const marginPersen = modalMurni > 0 && finalHargaJualPerPcs > 0 ? (marginNominal / modalMurni) * 100 : 0;
  
  const getAsaRecommendation = () => {
    const role = member?.role || 'default';
    const userName = member?.full_name ? member.full_name.split(' ')[0] : null;
    const isOwner = role === 'owner' || role === 'developer';
    const sapaan = isOwner ? (userName ? `Halo pak ${userName}!` : 'Halo pak!') : (userName ? `Halo kak ${userName}!` : 'Halo kak!');
    const qty = product.qty || 1;
    const namaVariant = (product.nama_product || '').toLowerCase();
    const ukMl = ukuranLabelMl || 0;
    let tgt;
    if (qty >= 10000) tgt = 12;
    else if (qty >= 5000) tgt = 18;
    else if (qty >= 3000) tgt = 25;
    else if (qty >= 2000) tgt = 30;
    else if (qty >= 1000) tgt = 40;
    else if (qty >= 500) tgt = 50;
    else if (qty >= 200) tgt = 60;
    else if (qty >= 100) tgt = 70;
    else if (qty >= 50) tgt = 80;
    else tgt = 100;
    if (namaVariant.includes('edp') || namaVariant.includes('eau de parfum') || namaVariant.includes('parfum')) tgt += 15;
    else if (namaVariant.includes('edt') || namaVariant.includes('eau de toilette')) tgt += 10;
    else if (namaVariant.includes('body mist') || namaVariant.includes('splash')) tgt += 5;
    else if (namaVariant.includes('refill') || namaVariant.includes('isi ulang')) tgt -= 10;
    if (ukMl >= 100) tgt += 5;
    else if (ukMl <= 15) tgt += 8;
    const asaPrice = Math.ceil((modalMurni * (1 + tgt / 100) + jasaNominal) / 500) * 500;
    const seed = Math.floor(modalMurni * 7 + qty * 3 + ukMl) % 10;
    const index = Math.abs(seed) % 3;
    let c = '', v = '';
    if (marginNominal < 0) {
      c = [`Ini minus Rp ${fmt(Math.abs(marginNominal))} per pcs, harus segera dinaikkin harganya ya.`, `Rugi Rp ${fmt(Math.abs(marginNominal))} per botol, ini nggak bisa dijual dengan harga segini.`, `Harga ini masih di bawah modal, rugi Rp ${fmt(Math.abs(marginNominal))}/pcs.`][index];
      v = `Asa rekomendasiin jual minimal di Rp ${fmt(asaPrice)} biar nggak rugi.`;
    } else if (marginPersen < 10) {
      c = [`Margin ${marginPersen.toFixed(1)}% ini terlalu tipis, hampir nggak ada untungnya.`, `${marginPersen.toFixed(1)}% doang? Ini masih terlalu mepet sama modal.`, `Margin segini tipis banget, perlu dinaikkin lagi harganya.`][index];
      v = `Asa saranin naikin ke Rp ${fmt(asaPrice)} biar ada ruang profit yang sehat.`;
    } else if (marginPersen < 30) {
      c = [`Margin ${marginPersen.toFixed(1)}% lumayan, tapi masih bisa lebih optimal.`, `${marginPersen.toFixed(1)}% oke buat ${qty >= 1000 ? 'volume segini' : 'batch ini'}, tapi masih ada ruang naikkin.`, `${marginPersen.toFixed(1)}% cukup buat jalan, tapi belum ideal.`][index];
      v = qty >= 1000 ? `Volume ${fmt(qty)} pcs masih wajar. Sweet-spot menurut Asa di Rp ${fmt(asaPrice)}.` : `Menurut Asa, jual di Rp ${fmt(asaPrice)} lebih optimal.`;
    } else if (marginPersen < 80) {
      c = [`Margin ${marginPersen.toFixed(1)}%, udah bagus dan sehat buat bisnis maklon.`, `${marginPersen.toFixed(1)}% — solid! Harga dan margin-nya udah seimbang.`, `Margin ${marginPersen.toFixed(1)}% ini udah bikin cashflow sehat, Asa suka.`][index];
      v = qty >= 500 ? `Untuk ${fmt(qty)} pcs, ini udah cuan dan kompetitif. Asa approve!` : `Harga sehat, margin bagus. Asa kasih jempol!`;
    } else if (marginPersen < 150) {
      c = [`Margin ${marginPersen.toFixed(1)}%, ini premium banget tapi masih masuk akal.`, `${marginPersen.toFixed(1)}% — harga premium, pastiin value produknya juga sepadan ya.`, `Margin ${marginPersen.toFixed(1)}%, termasuk tinggi. Pastikan kualitas produk mendukung harga ini.`][index];
      v = `Pastiin client ngerasa puas sama kualitasnya biar repeat order.`;
    } else {
      c = [`Margin ${marginPersen.toFixed(1)}%, ini sangat tinggi. Pastiin harga ini realistis untuk marketnya ya.`, `${marginPersen.toFixed(1)}% — wow, ini super premium. Cek lagi apakah client nyaman di harga segini.`, `Margin segini luar biasa tinggi, ${marginPersen.toFixed(1)}%. Perlu dipastikan client memang target market premium.`][index];
      v = `Pastiin client happy dan ngerasa worth it sama produknya.`;
    }
    return `${sapaan} ${c} ${v}`;
  };

  useEffect(() => {
    const qty = product.qty || 1;
    onChange(index, {
      jasa_nominal: product.jasa_nominal, 
      harga_jual_per_pcs: product.harga_jual_per_pcs,
      custom_ukuran_ml: product.custom_ukuran_ml,
      custom_ukuran_aktual_ml: product.custom_ukuran_aktual_ml,
      final_jasa_nominal: jasaNominal,
      final_harga_jual_per_pcs: finalHargaJualPerPcs,
      total_harga_jual: finalHargaJualPerPcs * qty,
      total_hpp_product: modalTotalPerPcs * qty,
      hpp_per_pcs_digunakan: modalTotalPerPcs,
      botol_harga_beli: modalBotol,
      tutup_harga_beli: modalTutup,
      spray_harga_beli: modalSpray,
      fullset_harga_beli: product.is_fullset && fullset ? fullset.harga_rupiah : 0,
    });
  }, [finalHargaJualPerPcs, jasaNominal, product.qty, product.jasa_nominal, product.harga_jual_per_pcs, product.custom_ukuran_ml, modalTotalPerPcs]);

  return (
    <div className="bg-white border border-[#E6D5B8] rounded-2xl overflow-hidden shadow-sm mb-4">
      {/* Selector Varian (Selalu Tampil Karena Ini Marketing Repeat Order) */}
      <div className="bg-[#F0FAFF] border-b border-[#BCE4FE] p-4 flex flex-col items-center">
        <p className="text-sm font-bold text-[#006699] mb-2 flex items-center gap-1">
          <Sparkles className="w-4 h-4" /> Cari Varian dari Database
        </p>
        <div className="w-full max-w-lg space-y-3">
          <SearchableSelect 
            options={varianDatabase.map(v => ({ value: v.id, label: v.nama_product }))}
            value={displayVariantId}
            onChange={(val) => {
              const varian = varianDatabase.find(v => String(v.id) === String(val));
              if (varian) {
                const isDiscoverySet = (varian.nama_product || '').toLowerCase().includes('discovery set');

                if (isDiscoverySet) {
                  // Map into multiple items
                  const mappedItems = varian.sizes.filter(s => s.racikan && s.racikan.length > 0).map(s => ({
                    nama_variant: s.size,
                    botol_id: s.botol_id || null,
                    tutup_id: s.tutup_id || null,
                    spray_id: s.spray_id || null,
                    botol_dari_client: s.botol_dari_client,
                    botol_ukuran_client: s.botol_ukuran_client,
                    is_fullset: s.is_fullset || false,
                    fullset_id: s.fullset_id || null,
                    fullset_nama: s.fullset_nama || null,
                    racikan: s.racikan || [],
                  }));
                  
                  const mappedKomponen = varian.sizes.filter(s => (!s.racikan || s.racikan.length === 0) && s.bahan).map(s => {
                    const kName = s.bahan?.nama_bahan || s.size;
                    const match = itemTambahanList.find(x => x.nama === kName);
                    return {
                      nama: match ? match.nama : kName,
                      item_nama: match ? match.nama : kName,
                      inventory_item_id: match ? match.id : null,
                      kategori: match ? match.kategori : (s.bahan?.kategori_bahan || 'Box/Kemasan'),
                      harga: match?.harga_rupiah || s.bahan?.custom_hpp || s.bahan?.harga_rupiah || 0,
                      harga_jual: match?.harga_jual || s.bahan?.custom_harga_jual || s.bahan?.harga_jual || 0,
                      qty: 1
                    };
                  }).filter(k => k.harga > 0 || k.harga_jual > 0);
                  
                  const attachedKomponen = varian.sizes.flatMap(s => s.komponen_tambahan || []).map(k => {
                    const match = itemTambahanList.find(x => x.nama === (k.nama || k.item_nama));
                    return {
                      ...k,
                      inventory_item_id: match ? match.id : null,
                      harga: match?.harga_rupiah || k.harga || 0,
                      harga_jual: match?.harga_jual || k.harga_jual || 0,
                    };
                  });
                  const finalKomponen = [...mappedKomponen, ...attachedKomponen];

                  onChange(index, {
                    variant_id: varian.id,
                    nama_product: varian.nama_product,
                    is_discovery_set: true,
                    items: mappedItems,
                    komponen_tambahan: finalKomponen,
                    jasa_nominal: undefined,
                    harga_jual_per_pcs: undefined,
                    // Reset single variant fields
                    botol_id: null,
                    tutup_id: null,
                    spray_id: null,
                    fullset_id: null,
                    is_fullset: false,
                    botol_dari_client: false,
                    botol_ukuran_client: 0,
                    racikan: [],
                    selected_size: 0,
                    custom_ukuran_ml: undefined,
                    custom_ukuran_aktual_ml: undefined
                  });
                } else {
                  const defaultSize = varian.sizes && varian.sizes.length > 0 ? varian.sizes[0] : null;
                  onChange(index, {
                    variant_id: varian.id,
                    nama_product: varian.nama_product,
                    is_discovery_set: false,
                    selected_size: defaultSize?.size || 0,
                    botol_id: defaultSize?.botol_id || null,
                    tutup_id: defaultSize?.tutup_id || null,
                    spray_id: defaultSize?.spray_id || null,
                    botol_dari_client: defaultSize ? defaultSize.botol_dari_client : true,
                    botol_ukuran_client: defaultSize ? defaultSize.botol_ukuran_client : 0,
                    is_fullset: defaultSize?.is_fullset || false,
                    fullset_id: defaultSize?.fullset_id || null,
                    fullset_nama: defaultSize?.fullset_nama || null,
                    racikan: defaultSize?.racikan || [],
                    komponen_tambahan: defaultSize?.komponen_tambahan || [],
                    jasa_nominal: undefined,
                    harga_jual_per_pcs: undefined
                  });
                }
              }
            }}
            placeholder="Ketik nama varian..."
          />

          {/* Jika sudah pilih varian (Bukan Discovery Set), tampilkan pilihan ukuran */}
          {product.variant_id && !product.is_discovery_set && (
            <div className="bg-white p-3 rounded-xl border border-[#BCE4FE]">
              <label className="text-xs font-bold text-[#006699] mb-2 block">Pilih Ukuran Varian (ml)</label>
              <div className="flex flex-wrap gap-2">
                {(varianDatabase.find(v => v.id === product.variant_id)?.sizes || []).map(s => (
                  <button
                    key={s.size}
                    type="button"
                    onClick={() => {
                      onChange(index, {
                        selected_size: s.size,
                        botol_id: s.botol_id,
                        tutup_id: s.tutup_id,
                        spray_id: s.spray_id,
                        botol_dari_client: s.botol_dari_client,
                        botol_ukuran_client: s.botol_ukuran_client,
                        is_fullset: s.is_fullset || false,
                        fullset_id: s.fullset_id || null,
                        fullset_nama: s.fullset_nama || null,
                        racikan: s.racikan || [],
                        komponen_tambahan: s.komponen_tambahan || [],
                        jasa_nominal: undefined,
                        harga_jual_per_pcs: undefined
                      });
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${product.selected_size === s.size ? 'bg-[#006699] text-white border-[#006699]' : 'bg-white text-[#006699] border-[#BCE4FE] hover:bg-[#F0FAFF]'}`}
                  >
                    {s.size} ml
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {product.is_discovery_set ? (
        <FinanceInvoiceCard
          product={product}
          index={index}
          botolList={botolList}
          tutupList={tutupList}
          sprayList={sprayList}
          bahanList={bahanList}
          itemTambahanList={itemTambahanList}
          fullsetList={fullsetList}
          isSampleMode={false}
          isDatabaseMode={true}
          hideWrapper={true}
          onChange={(data) => onChange(index, data)}
          onRemove={onRemove}
          canRemove={canRemove}
        />
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-[#F9F5EC] border-b border-[#E6D5B8]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-white font-bold text-sm shadow-sm">{index + 1}</div>
          <div>
            <Input
              value={product.nama_product}
              onChange={e => onChange(index, { nama_product: e.target.value })}
              placeholder="Contoh: Sun Zest Serum"
              className="font-bold text-[#302621] text-base border-0 bg-transparent p-0 h-auto focus-visible:ring-0 w-64 placeholder:text-[#A39171]"
            />
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              {isValid && (
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                  <span className="text-xs text-green-600 font-medium">Formula Lengkap (100%)</span>
                </div>
              )}
              {isAnyItemMissingInDB && (
                <div className="flex items-center gap-1">
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-900 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-md shadow-2xs">
                    <AlertCircle className="w-3 h-3 text-amber-600 shrink-0" />
                    {missingBadgeLabel}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <Button variant="ghost" size="sm" onClick={onRemove} className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs">Hapus</Button>
          )}
          <Button variant="ghost" size="icon" className="w-8 h-8 text-[#8C6208] hover:bg-[#E6D5B8]/50" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="p-6 space-y-6">
          <div className="p-4 -mx-6 -mt-6 border-b border-[#E6D5B8] bg-[#FDFBF7]">
            {/* Info Kuantitas + Ukuran Botol */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold text-[#7A695B] uppercase tracking-wider mb-1 block">Kuantitas Pesanan</label>
                <Input type="number" min="1" value={product.qty === '' ? '' : (product.qty || '')} onChange={e => onChange(index, { qty: e.target.value === '' ? '' : parseInt(e.target.value) })} className="h-10 border-[#E6D5B8] focus-visible:ring-[#D4AF37]" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#7A695B] uppercase tracking-wider mb-1 block">Label (ml)</label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    min="0"
                    step="0.1"
                    value={product.custom_ukuran_ml !== undefined ? product.custom_ukuran_ml : (baseUkuran || '')}
                    onChange={e => onChange(index, { custom_ukuran_ml: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                    className="h-10 border-[#E6D5B8] focus-visible:ring-[#D4AF37]"
                  />
                  <span className="text-sm font-semibold text-[#7A695B]">ml</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1 block">Isi Aktual (ml)</label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    min="0"
                    step="0.1"
                    value={product.custom_ukuran_aktual_ml !== undefined ? product.custom_ukuran_aktual_ml : ''}
                    onChange={e => onChange(index, { custom_ukuran_aktual_ml: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                    placeholder={product.custom_ukuran_ml || baseUkuran || "Isi (ml)"}
                    className="h-10 border-blue-200 focus-visible:ring-blue-400 bg-blue-50/30"
                  />
                  <span className="text-sm font-semibold text-blue-600">ml</span>
                </div>
              </div>
            </div>
          </div>

          {/* Kemasan (Editable) */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-bold text-[#302621] flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-[#F9F5EC] flex items-center justify-center">
                  <Package strokeWidth={1.5} className="w-4 h-4 text-[#8C6208]" />
                </div>
                Kemasan & Packaging
              </h4>
              <button 
                type="button" 
                onClick={() => onChange(index, { is_fullset: !product.is_fullset })}
                className="text-xs text-[#8C6208] hover:text-[#7A5506] font-bold border border-[#E6D5B8] px-3 py-1.5 rounded-lg bg-white"
              >
                {product.is_fullset ? "Pilih Terpisah" : "Gunakan Paket Fullset"}
              </button>
            </div>
            
            {product.is_fullset ? (
              <div className="mb-4 bg-emerald-50/50 border border-emerald-100 rounded-xl p-4">
                <label className="text-xs font-semibold text-emerald-800 mb-1.5 block">Pilih Fullset</label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'Tanpa Fullset / Dari Client' },
                    ...(isFullsetMissingInDB ? [{
                      value: '__missing_fullset__',
                      label: `${product.fullset_nama} (Item database brand tidak ada di inventory)`,
                      labelName: `${product.fullset_nama} (Tidak ada di inventory)`,
                      isMissing: true
                    }] : []),
                    ...fullsetList.map(f => ({ value: f.id, label: `${f.nama} (Rp ${fmt(f.harga_jual || f.harga_rupiah || 0)})` }))
                  ]}
                  value={fullset?.id || product.fullset_id || (isFullsetMissingInDB ? '__missing_fullset__' : '')}
                  onChange={(val) => {
                    if (val === '__missing_fullset__') return;
                    const fset = fullsetList.find(f => String(f.id) === String(val));
                    if (fset) {
                      onChange(index, {
                        fullset_id: val,
                        fullset_nama: fset.nama,
                        fullset_harga_jual: getHargaJual(fset),
                        fullset_harga_modal: fset.harga_rupiah
                      });
                    } else {
                      onChange(index, {
                        fullset_id: val,
                        fullset_nama: ''
                      });
                    }
                  }}
                  placeholder="Cari fullset..."
                  className="bg-white/50 border-[#E6D5B8]"
                />
                {isFullsetMissingInDB ? (
                  <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded-lg p-2.5 mt-2 flex items-start gap-2 shadow-xs">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold block text-amber-900 leading-tight">Item yang dipilih di database brand tidak ada di inventory</span>
                      <span className="text-[10px] text-amber-700 mt-0.5 block">Nama di Database Brand: <strong>{product.fullset_nama}</strong></span>
                    </div>
                  </div>
                ) : product.fullset_id && (() => {
                  const fset = fullsetList.find(f => String(f.id) === String(product.fullset_id));
                  if (fset) {
                    return (
                      <div className="mt-3 bg-white p-3 rounded-lg border border-[#E6D5B8] flex justify-between items-center shadow-sm">
                        <div className="space-y-1">
                          <p className="text-[10px] font-medium text-[#7A695B]">Harga Beli: <span className="font-bold text-[#302621]">Rp {fmt(fset.harga_rupiah || 0)}</span></p>
                          <p className="text-[10px] font-medium text-[#7A695B]">Harga Jual: <span className="font-bold text-[#8C6208]">Rp {fmt(fset.harga_jual || 0)}</span></p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ) : (
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl border border-[#E6D5B8]/50 bg-slate-50/50`}>
                <div>
                  <label className="text-xs font-semibold text-[#4A3B32] mb-1.5 block">Botol / Wadah</label>
                  <SearchableSelect
                    options={[
                      { value: '', label: 'Dari Client', labelName: 'Dari Client', isClient: true },
                      ...(isBotolMissingInDB ? [{
                        value: '__missing_botol__',
                        label: `${product.botol_nama} (Item database brand tidak ada di inventory)`,
                        labelName: `${product.botol_nama} (Tidak ada di inventory)`,
                        isMissing: true
                      }] : []),
                      ...botolList.map(b => ({ 
                        value: b.id, 
                        label: `${b.nama} (${b.ukuran_label_ml}ml)`,
                        labelName: `${b.nama} (${b.ukuran_label_ml}ml)`,
                        modal: b.harga_rupiah || 0,
                        jual: b.harga_jual || 0
                      }))
                    ]}
                    renderOption={(opt) => (
                      <div className="py-0.5" style={{ pointerEvents: 'none' }}>
                        <div className={`font-medium ${opt.isMissing ? 'text-amber-800 font-bold' : 'text-[#302621]'}`}>{opt.labelName}</div>
                        {opt.isMissing ? (
                          <div className="text-[10px] text-amber-700 font-semibold mt-0.5">
                            Item yang dipilih di database brand tidak ada di inventory
                          </div>
                        ) : !opt.isClient && (
                          <div className="text-[10px] text-[#7A695B] flex gap-3 mt-1">
                            <span>Modal: Rp {fmt(opt.modal)}</span>
                            <span className="font-bold text-[#8C6208]">Jual: Rp {fmt(opt.jual)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    value={product.botol_id || (isBotolMissingInDB ? '__missing_botol__' : '')}
                    onChange={(val) => {
                      if (val === '__missing_botol__') return;
                      const btl = botolList.find(b => String(b.id) === String(val));
                      if (btl) {
                        const updateData = {
                          botol_id: val,
                          botol_nama: btl.nama,
                          botol_dari_client: false
                        };
                        if (!product.custom_ukuran_ml) updateData.custom_ukuran_ml = btl.ukuran_label_ml;
                        if (!product.custom_ukuran_aktual_ml) updateData.custom_ukuran_aktual_ml = btl.ukuran_aktual_ml;
                        onChange(index, updateData);
                      } else {
                        onChange(index, {
                          botol_id: val,
                          botol_nama: 'Dari Client',
                          botol_dari_client: true
                        });
                      }
                    }}
                    placeholder="Pilih Botol"
                    className="bg-white"
                  />
                  {isBotolMissingInDB ? (
                    <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded-lg p-2.5 mt-1.5 flex items-start gap-2 shadow-xs">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold block text-amber-900 leading-tight">Item yang dipilih di database brand tidak ada di inventory</span>
                        <span className="text-[10px] text-amber-700 mt-0.5 block">Nama di Database Brand: <strong>{product.botol_nama}</strong></span>
                      </div>
                    </div>
                  ) : !product.botol_id ? (
                    <div className="text-xs text-[#7A695B] mt-1 italic">Dari Client</div>
                  ) : (() => {
                    const btl = botolList.find(b => String(b.id) === String(product.botol_id));
                    if (btl) {
                      return (
                        <div className="mt-2 pl-1 space-y-1">
                          <p className="text-[10px] font-medium text-[#7A695B]">Harga Beli: <span className="font-bold text-[#302621]">Rp {fmt(btl.harga_rupiah || 0)}</span></p>
                          <p className="text-[10px] font-medium text-[#7A695B]">Harga Jual: <span className="font-bold text-[#8C6208]">Rp {fmt(btl.harga_jual || 0)}</span></p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#4A3B32] mb-1.5 block">Tutup (Cap)</label>
                  <SearchableSelect
                    options={[
                      { value: '', label: 'Dari Client', labelName: 'Dari Client', isClient: true },
                      ...(isTutupMissingInDB ? [{
                        value: '__missing_tutup__',
                        label: `${product.tutup_nama} (Item database brand tidak ada di inventory)`,
                        labelName: `${product.tutup_nama} (Tidak ada di inventory)`,
                        isMissing: true
                      }] : []),
                      ...tutupList.map(t => ({ 
                        value: t.id, 
                        label: t.nama,
                        labelName: t.nama,
                        modal: t.harga_rupiah || 0,
                        jual: t.harga_jual || 0
                      }))
                    ]}
                    renderOption={(opt) => (
                      <div className="py-0.5" style={{ pointerEvents: 'none' }}>
                        <div className={`font-medium ${opt.isMissing ? 'text-amber-800 font-bold' : 'text-[#302621]'}`}>{opt.labelName}</div>
                        {opt.isMissing ? (
                          <div className="text-[10px] text-amber-700 font-semibold mt-0.5">
                            Item yang dipilih di database brand tidak ada di inventory
                          </div>
                        ) : !opt.isClient && (
                          <div className="text-[10px] text-[#7A695B] flex gap-3 mt-1">
                            <span>Modal: Rp {fmt(opt.modal)}</span>
                            <span className="font-bold text-[#8C6208]">Jual: Rp {fmt(opt.jual)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    value={product.tutup_id || (isTutupMissingInDB ? '__missing_tutup__' : '')}
                    onChange={(val) => {
                      if (val === '__missing_tutup__') return;
                      const ttp = tutupList.find(t => String(t.id) === String(val));
                      if (ttp) {
                        onChange(index, {
                          tutup_id: val,
                          tutup_nama: ttp.nama,
                          tutup_dari_client: false
                        });
                      } else {
                        onChange(index, {
                          tutup_id: val,
                          tutup_nama: 'Dari Client',
                          tutup_dari_client: true
                        });
                      }
                    }}
                    placeholder="Pilih Tutup"
                    className="bg-white"
                  />
                  {isTutupMissingInDB ? (
                    <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded-lg p-2.5 mt-1.5 flex items-start gap-2 shadow-xs">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold block text-amber-900 leading-tight">Item yang dipilih di database brand tidak ada di inventory</span>
                        <span className="text-[10px] text-amber-700 mt-0.5 block">Nama di Database Brand: <strong>{product.tutup_nama}</strong></span>
                      </div>
                    </div>
                  ) : !product.tutup_id ? (
                    <div className="text-xs text-[#7A695B] mt-1 italic">Dari Client</div>
                  ) : (() => {
                    const ttp = tutupList.find(t => String(t.id) === String(product.tutup_id));
                    if (ttp) {
                      return (
                        <div className="mt-2 pl-1 space-y-1">
                          <p className="text-[10px] font-medium text-[#7A695B]">Harga Beli: <span className="font-bold text-[#302621]">Rp {fmt(ttp.harga_rupiah || 0)}</span></p>
                          <p className="text-[10px] font-medium text-[#7A695B]">Harga Jual: <span className="font-bold text-[#8C6208]">Rp {fmt(ttp.harga_jual || 0)}</span></p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#4A3B32] mb-1.5 block">Spray / Pump</label>
                  <SearchableSelect
                    options={[
                      { value: '', label: 'Dari Client', labelName: 'Dari Client', isClient: true },
                      ...(isSprayMissingInDB ? [{
                        value: '__missing_spray__',
                        label: `${product.spray_nama} (Item database brand tidak ada di inventory)`,
                        labelName: `${product.spray_nama} (Tidak ada di inventory)`,
                        isMissing: true
                      }] : []),
                      ...sprayList.map(s => ({ 
                        value: s.id, 
                        label: s.nama,
                        labelName: s.nama,
                        modal: s.harga_rupiah || 0,
                        jual: s.harga_jual || 0
                      }))
                    ]}
                    renderOption={(opt) => (
                      <div className="py-0.5" style={{ pointerEvents: 'none' }}>
                        <div className={`font-medium ${opt.isMissing ? 'text-amber-800 font-bold' : 'text-[#302621]'}`}>{opt.labelName}</div>
                        {opt.isMissing ? (
                          <div className="text-[10px] text-amber-700 font-semibold mt-0.5">
                            Item yang dipilih di database brand tidak ada di inventory
                          </div>
                        ) : !opt.isClient && (
                          <div className="text-[10px] text-[#7A695B] flex gap-3 mt-1">
                            <span>Modal: Rp {fmt(opt.modal)}</span>
                            <span className="font-bold text-[#8C6208]">Jual: Rp {fmt(opt.jual)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    value={product.spray_id || (isSprayMissingInDB ? '__missing_spray__' : '')}
                    onChange={(val) => {
                      if (val === '__missing_spray__') return;
                      const spr = sprayList.find(s => String(s.id) === String(val));
                      if (spr) {
                        onChange(index, {
                          spray_id: val,
                          spray_nama: spr.nama,
                          spray_dari_client: false
                        });
                      } else {
                        onChange(index, {
                          spray_id: val,
                          spray_nama: 'Dari Client',
                          spray_dari_client: true
                        });
                      }
                    }}
                    placeholder="Pilih Spray"
                    className="bg-white"
                  />
                  {isSprayMissingInDB ? (
                    <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded-lg p-2.5 mt-1.5 flex items-start gap-2 shadow-xs">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold block text-amber-900 leading-tight">Item yang dipilih di database brand tidak ada di inventory</span>
                        <span className="text-[10px] text-amber-700 mt-0.5 block">Nama di Database Brand: <strong>{product.spray_nama}</strong></span>
                      </div>
                    </div>
                  ) : !product.spray_id ? (
                    <div className="text-xs text-[#7A695B] mt-1 italic">Dari Client</div>
                  ) : (() => {
                    const spr = sprayList.find(s => String(s.id) === String(product.spray_id));
                    if (spr) {
                      return (
                        <div className="mt-2 pl-1 space-y-1">
                          <p className="text-[10px] font-medium text-[#7A695B]">Harga Beli: <span className="font-bold text-[#302621]">Rp {fmt(spr.harga_rupiah || 0)}</span></p>
                          <p className="text-[10px] font-medium text-[#7A695B]">Harga Jual: <span className="font-bold text-[#8C6208]">Rp {fmt(spr.harga_jual || 0)}</span></p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
            
            {/* Item Tambahan (Read Only dari DB) */}
            {(product.komponen_tambahan && product.komponen_tambahan.length > 0) && (
              <div className="mt-4 bg-[#FDFBF7] border border-[#E6D5B8]/50 rounded-xl p-4">
                <label className="text-xs font-semibold text-[#4A3B32] mb-3 block">Item Tambahan Kemasan</label>
                <div className="space-y-2">
                  {product.komponen_tambahan.map((k, idx) => {
                    const price = getKomponenPrice(k);
                    return (
                    <div key={idx} className="bg-white border border-[#E6D5B8]/70 rounded-lg p-3 flex justify-between items-center shadow-sm">
                       <div>
                         <div className="font-bold text-[#302621] text-sm flex items-center gap-2">
                           {k.item_nama || k.nama_item || k.nama}
                           <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider">
                             {k.kategori}
                           </span>
                           {k.dari_client && (
                             <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider">
                               Dari Client
                             </span>
                           )}
                         </div>
                         {k.catatan && <div className="text-[11px] text-[#7A695B] mt-0.5 italic">"{k.catatan}"</div>}
                       </div>
                       <div className="text-right">
                         {!k.dari_client ? (
                           <>
                             <div className="text-xs font-black text-[#8C6208]">Rp {fmt(price.jual)} / {k.satuan || 'pcs'}</div>
                             <div className="text-[10px] text-[#7A695B] font-medium mt-0.5">Modal: Rp {fmt(price.modal)}</div>
                           </>
                         ) : (
                           <div className="text-xs font-black text-emerald-600">Rp 0</div>
                         )}
                       </div>
                    </div>
                  )})}
                </div>
              </div>
            )}
          </div>

          {/* Formula Racikan (Read Only dari DB) */}
          <div>
            <h4 className="text-sm font-bold flex items-center gap-2 text-[#302621] mb-3">
              <div className="w-6 h-6 rounded-md bg-[#F9F5EC] flex items-center justify-center">
                <FlaskConical strokeWidth={1.5} className="w-4 h-4 text-[#8C6208]" />
              </div>
              Komposisi Cairan
              {ukuranLabel > 0 && <Badge variant="outline" className="text-xs bg-white text-[#7A695B]">Acuan: {ukuranLabel}ml</Badge>}
            </h4>
            {(product.racikan || []).length > 0 ? (
              <div className="bg-[#F9F5EC]/30 border border-[#E6D5B8] rounded-xl px-2 py-2 space-y-1">
                {(product.racikan || []).map((row, i) => {
                  const hSatuan = (row.satuan || 'kg').toLowerCase();
                  const satuanLabel = hSatuan === 'g' ? 'liter' : (row.satuan || 'kg');
                  const hBeliSat = row.harga_per_satuan || 0;
                  const hJual = row.harga_jual_satuan !== undefined ? row.harga_jual_satuan : 0;
                  const hBeliPerKecil = (hSatuan === 'gram') ? hBeliSat : hBeliSat / 1000;
                  const hJualPerKecil = row.custom_harga_per_unit || ((hSatuan === 'gram') ? hJual : hJual / 1000);
                  const isLiterBahan = hSatuan === 'g';
                  const volMl = row.volume_ml || ((safeFloat(row.persentase) / 100) * ukuranLabel);
                  const volGram = row.volume_gram || (isLiterBahan ? volMl * 0.8 : 0);
                  const hppBeli = hBeliPerKecil * volMl;
                  const hppJual = hJualPerKecil * volMl;
                  
                  return (
                    <div key={i} className="py-2.5 px-3 hover:bg-white transition-colors rounded-lg border-b border-[#E6D5B8]/30 last:border-0 space-y-2">
                      {/* Main row */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-bold bg-[#E6D5B8]/40 text-[#8C6208]">
                            {row.kategori}
                          </span>
                          <span className="font-semibold text-[13px] text-[#302621]">{row.bahan_nama || 'Tanpa Nama'}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-black text-[#8C6208] text-[13px] bg-[#F9F5EC] px-2 py-0.5 rounded shadow-sm border border-[#E6D5B8]/50">{row.persentase}%</span>
                          {volMl > 0 && (
                            isLiterBahan ? (
                              <div className="text-right flex flex-col items-end">
                                <span className="text-[12px] font-bold text-amber-700 leading-none">{parseFloat(volGram.toFixed(2))}g</span>
                                <span className="text-[9px] text-muted-foreground">/{parseFloat(volMl.toFixed(2))}ml</span>
                              </div>
                            ) : (
                              <span className="text-[12px] font-bold text-amber-700">{parseFloat(volMl.toFixed(2))} ml</span>
                            )
                          )}
                        </div>
                      </div>
                      {/* Detail harga */}
                      {!row.dari_client ? (
                        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[11px] text-muted-foreground">
                          <div className="flex items-center gap-1.5 bg-slate-50/50 border border-slate-100 rounded px-1.5 py-0.5">
                            <span className="font-medium text-slate-600">Beli:</span>
                            <span className="text-slate-500">Rp {fmt(hBeliSat)}/{satuanLabel}</span>
                          </div>
                          <span className="text-slate-300">|</span>
                          <span className="text-slate-600">
                            Harga Jual: <span className="font-medium">Rp {fmt(hJual)} /{satuanLabel}</span>
                          </span>
                          <span className="text-slate-300">→</span>
                          <span className="text-slate-600 font-medium">
                            Rp {fmt(hJualPerKecil)} /g
                          </span>
                          <span className="text-slate-300">→</span>
                          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded px-2 py-0.5 shadow-sm ml-auto">
                            <span className="text-slate-500">
                              Modal: Rp {fmt(hppBeli)}
                            </span>
                            <span className="text-slate-300">|</span>
                            <span className="text-emerald-700 font-bold">
                              Jual: Rp {fmt(hppJual)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 shadow-sm">
                            <CheckCircle2 className="w-3 h-3" /> Dari Client — Modal: Rp 0
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-[#7A695B]">Pilih varian untuk melihat formula.</p>
            )}
          </div>

          {/* Setup Jasa & Harga Jual Akhir */}
          {(product.is_discovery_set || (isValid && ukuranLabel > 0)) && (
            <div className="bg-gradient-to-br from-[#302621] to-[#1A1411] text-[#FFFDF9] rounded-xl p-6 shadow-xl relative overflow-hidden mt-6">
              <h3 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider mb-4 border-b border-white/10 pb-3">
                Penentuan Harga Jual (Invoice)
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 relative z-10">
                <div className="bg-white/10 p-4 rounded-xl border border-white/20">
                  <label className="text-xs text-[#D4AF37] font-bold mb-1.5 block">Biaya Jasa Produksi / pcs</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/70">Rp</span>
                    <Input 
                      type="number"
                      value={product.jasa_nominal === undefined ? '' : jasaNominal}
                      onChange={e => onChange(index, { jasa_nominal: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                      className="h-10 bg-transparent border-white/30 text-white placeholder:text-white/30 focus-visible:ring-[#D4AF37]" 
                      placeholder={`Default: Rp ${fmt(otomatisJasa)}`}
                    />
                  </div>
                  <p className="text-[10px] text-white/50 mt-1.5">Jasa dihitung sepenuhnya sebagai Margin Profit Internal.</p>
                </div>
                
                <div className="bg-[#D4AF37]/10 p-4 rounded-xl border border-[#D4AF37]/30">
                  <label className="text-xs text-[#D4AF37] font-bold mb-1.5 block">Harga Jual Akhir / pcs</label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#D4AF37]">Rp</span>
                    <Input 
                      type="number"
                      value={product.harga_jual_per_pcs === undefined ? '' : product.harga_jual_per_pcs}
                      onChange={e => onChange(index, { harga_jual_per_pcs: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                      className="h-10 bg-white/5 border-[#D4AF37]/50 text-white font-bold placeholder:text-white/30 focus-visible:ring-[#D4AF37]" 
                      placeholder={(customHargaJual || (customMargin !== undefined && customMargin !== null && customMargin !== '')) ? `Dari DB: Rp ${fmt(defaultHargaJualAkhir)}` : `Sistem: Rp ${fmt(defaultHargaJualAkhir)}`}
                    />
                  </div>
                  <p className="text-[10px] text-white/50 mt-1.5">Harga yang akan ditagihkan ke Client (Customer).</p>
                </div>
              </div>

              {/* ASA AI Recommendation */}
              <div className="bg-[#FFFDF9] border border-[#E6D5B8] rounded-xl p-4 shadow-sm mb-6 relative z-10">
                <div className="flex items-center gap-2 text-[#8C6208] mb-2 font-bold text-xs">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8C6208] flex items-center justify-center shrink-0">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                  ASA (AI) Rekomendasi
                </div>
                <p className="text-xs text-[#7A695B] leading-relaxed italic border-l-2 border-[#D4AF37] pl-3">"{getAsaRecommendation()}"</p>
              </div>

              {/* Rincian Harga per pcs */}
              <div className="space-y-2 text-xs text-white/80 mb-6 relative z-10 border-t border-white/10 pt-4">
                <div className="flex justify-between items-center text-white/50">
                  <span>Modal Kemasan (Packaging)</span>
                  <span>Rp {fmt(modalPackaging)}</span>
                </div>
                {(product.is_fullset && jualPackagingManual > modalPackaging) && (
                  <div className="flex justify-between items-center text-emerald-400 pt-1">
                    <span>Margin Jual Fullset</span>
                    <span>Rp {fmt(jualPackagingManual - modalPackaging)}</span>
                  </div>
                )}
                {(!product.is_fullset && (jualBotol > modalBotol || jualTutup > modalTutup || jualSpray > modalSpray)) && (
                  <div className="flex justify-between items-center text-emerald-400 pt-1">
                    <span>Margin Jual Kemasan</span>
                    <span>Rp {fmt((jualBotol - modalBotol) + (jualTutup - modalTutup) + (jualSpray - modalSpray))}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-white/50 mt-1">
                  <span>Modal Formula Cairan ({ukuranLabel}ml)</span>
                  <span>Rp {fmt(modalRacikan)}</span>
                </div>
                {jualRacikan > modalRacikan && (
                  <div className="flex justify-between items-center text-emerald-400 pt-1">
                    <span>Margin Jual Cairan</span>
                    <span>Rp {fmt(jualRacikan - modalRacikan)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-white/50 mt-1">
                  <span>Modal Kelengkapan Tambahan</span>
                  <span>Rp {fmt(modalKomponen)}</span>
                </div>
                {jualKomponen > modalKomponen && (
                  <div className="flex justify-between items-center text-emerald-400 pt-1">
                    <span>Margin Kelengkapan Tambahan</span>
                    <span>Rp {fmt(jualKomponen - modalKomponen)}</span>
                  </div>
                )}
                {jasaNominal > 0 && (
                  <div className="flex justify-between items-center text-[#D4AF37] mt-1">
                    <span>Biaya Jasa Produksi</span>
                    <span>Rp {fmt(jasaNominal)}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center pt-2 font-bold text-white border-t border-white/10 mt-2">
                  <span>Total Harga Sistem (per pcs)</span>
                  <span>Rp {fmt(pureSistemTotal)}</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 items-end relative z-10">
                <div className={`p-3 rounded-xl border ${marginNominal >= 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                  <p className="text-[10px] text-white/70 mb-1">Margin Profit (per pcs)</p>
                  <p className={`text-xl font-bold ${marginNominal >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {marginNominal >= 0 ? '+' : ''}Rp {fmt(marginNominal)}
                  </p>
                  <p className={`text-[10px] ${marginNominal >= 0 ? 'text-emerald-400/70' : 'text-red-400/70'} mt-0.5`}>
                    {marginPersen.toFixed(1)}% dari Total Biaya Beli
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/70 mb-1">Grand Total ({product.qty || 1} pcs)</p>
                  <p className="text-3xl font-black text-[#D4AF37]">Rp {fmt(finalHargaJualPerPcs * (product.qty || 1))}</p>
                </div>
              </div>

              {/* Kesimpulan */}
              <div className="mt-6 pt-6 border-t border-white/20 relative z-10">
                <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider mb-4">
                  Kesimpulan ({product.qty || 1} pcs)
                </h4>
                
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${(product.komponen_tambahan && product.komponen_tambahan.length > 0) ? 'lg:grid-cols-3' : ''} gap-4 mb-4`}>
                  {/* Kemasan */}
                  {aggregatedKemasan.length > 0 && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-white/80">
                      <p className="font-bold text-white mb-2 pb-1 border-b border-white/10">Kemasan</p>
                      {aggregatedKemasan.map((k, i) => (
                        <div key={i} className="flex justify-between items-center mt-2">
                          <span className="truncate pr-2">{k.nama}</span>
                          <span className="shrink-0 text-right font-medium">{fmtQty((product.qty || 1) * k.qty)} pcs <span className="text-white/50 text-[10px] block font-normal">Rp {fmt((product.qty || 1) * (k.jual !== undefined ? k.jual : k.modal))}</span></span>
                        </div>
                      ))}
                      <div className="flex justify-between mt-3 pt-2 border-t border-white/10 font-bold text-[#D4AF37]">
                        <span>Total Kemasan</span>
                        <span>Rp {fmt((product.qty || 1) * jualPackagingManual)}</span>
                      </div>
                    </div>
                  )}

                  {/* Formula Cairan */}
                  {aggregatedCairan.length > 0 && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-white/80">
                      <p className="font-bold text-white mb-2 pb-1 border-b border-white/10">Cairan{product.is_discovery_set ? '' : ` (${ukuranLabel}ml)`}</p>
                      
                      <div className="space-y-2 mt-2">
                      {aggregatedCairan.map((r, i) => {
                        const totalVol = r.volPerPcs * (product.qty || 1);
                        
                        const isFluid = r.satuan === 'liter' || r.satuan === 'g' || r.kategori?.toLowerCase().includes('alkohol') || r.kategori?.toLowerCase().includes('aqua');
                        const unitLarge = isFluid ? 'L' : 'kg';
                        const unitSmall = isFluid ? 'ml' : 'g';
                        const displayVol = totalVol >= 1000 ? `${(totalVol / 1000).toFixed(2).replace(/\.00$/, '')} ${unitLarge}` : `${totalVol.toFixed(1).replace(/\.0$/, '')} ${unitSmall}`;
                        
                        const price = (r.jualCost !== undefined ? r.jualCost : r.cost) * (product.qty || 1);

                        return (
                          <div key={i} className="flex justify-between items-center">
                            <span className="truncate pr-2">{r.bahan_nama}</span>
                            <span className="shrink-0 text-right font-medium">{displayVol} <span className="text-white/50 text-[10px] block font-normal">Rp {fmt(price)}</span></span>
                          </div>
                        );
                      })}
                      </div>
                      
                      <div className="flex justify-between mt-3 pt-2 border-t border-white/10 font-bold text-[#D4AF37]">
                        <span>Total Cairan</span>
                        <span>Rp {fmt((product.qty || 1) * jualRacikan)}</span>
                      </div>
                    </div>
                  )}

                  {/* Tambahan & Jasa */}
                  {(product.komponen_tambahan && product.komponen_tambahan.length > 0) && (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-white/80">
                      <p className="font-bold text-white mb-2 pb-1 border-b border-white/10">Kelengkapan Tambahan</p>
                      
                      <div className="space-y-2 mt-2">
                        {product.komponen_tambahan.map((k, i) => (
                          <div key={i} className="flex justify-between items-center">
                            <span className="truncate pr-2">{k.nama || k.kategori}</span>
                            <span className="shrink-0 text-right font-medium">{fmtQty((product.qty || 1) * (k.qty || 1))} pcs <span className="text-white/50 text-[10px] block font-normal">Rp {fmt((product.qty || 1) * (k.qty || 1) * (getKomponenPrice(k).jual))}</span></span>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-between mt-3 pt-2 border-t border-white/10 font-bold text-[#D4AF37]">
                        <span>Total Tambahan</span>
                        <span>Rp {fmt((product.qty || 1) * jualKomponen)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 text-xs">
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="space-y-1.5 flex-1">
                      <div className="flex justify-between text-white/70">
                        <span>Total Harga Beli Keseluruhan</span>
                        <span>Rp {fmt((product.qty || 1) * modalMurni)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-[#D4AF37]">
                        <span>Total Harga Jual Keseluruhan</span>
                        <span>Rp {fmt((product.qty || 1) * finalHargaJualPerPcs)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-emerald-400 pt-1 border-t border-white/10 mt-1">
                        <span>Total Margin Profit</span>
                        <span>Rp {fmt((product.qty || 1) * marginNominal)} ({marginPersen.toFixed(1)}%)</span>
                      </div>
                    </div>
                    <div className="sm:w-1/3 bg-black/20 p-3 rounded-lg border border-white/5 flex flex-col justify-center items-center text-center">
                       <p className="text-white/60 mb-1">Catatan Profit:</p>
                       <p className="text-[#D4AF37] font-bold">Margin profit keseluruhan ini sudah termasuk pendapatan dari Biaya Jasa Produksi sebesar Rp {fmt((product.qty || 1) * jasaNominal)}.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}
